import type { ReturnRequestStatusDto } from "@babackapp/private-api-client/types";
import { Badge } from "@babackapp/ui/components/badge";
import { useMemo } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import useViewerRole from "@/lib/rights/use-viewer-role.ts";
import { statusColors } from "@/pages/(app)/returns/hooks/use-filter-definitions.tsx";
import getStatusFromReturnRequest from "@/pages/(app)/returns/list/utils/get-status-from-return-request.ts";
import { useReturnRequestsFilters } from "@/pages/(app)/returns/providers/use-return-requests-filters";
import { cn } from "@/utils/cn";

type ReturnStatusBadgeProps = {
  statuses: ReturnRequestStatusDto;
  size?: "sm" | "md";
};

export default function ReturnStatusBadge({
  statuses: returnRequestStatuses,
  size = "md",
}: ReturnStatusBadgeProps) {
  const { data: shop } = useCurrentShopSuspense();
  const role = useViewerRole();
  const status = getStatusFromReturnRequest(returnRequestStatuses);

  const { filtersDefinition } = useReturnRequestsFilters();
  const statuses = filtersDefinition.get("status")!;
  const statusFound = statuses.options.get(status);

  const getStatusColorForCurrentRole = useMemo(() => {
    if (!role || !statusFound) {
      return {
        color: statusFound?.color,
        background: statusFound?.background,
      };
    }

    const { activated: workflowLogistician } = shop.workflowLogistician;
    const { value: statusName } = statusFound;

    if (["store", "admin", "owner", "customer_support"].includes(role)) {
      if (
        statusName === "created" ||
        statusName === "treatedLogistician" ||
        statusName === "partiallyTreatedLogistician" ||
        (statusName === "shipped" && !workflowLogistician)
      ) {
        return statusColors.amber;
      }
    }

    if (role === "logistician") {
      if (
        statusName === "treatedLogistician" ||
        statusName === "partiallyTreatedMerchant"
      ) {
        return statusColors.green;
      }

      if (
        statusName === "partiallyTreatedLogistician" ||
        statusName === "shipped"
      ) {
        return statusColors.amber;
      }
    }

    return {
      color: statusFound?.color,
      background: statusFound?.background,
    };
  }, [role, shop.workflowLogistician, statusFound]);

  if (!statusFound) {
    return null;
  }

  if (size === "sm") {
    return (
      <Badge
        variant="outline"
        size="xs"
        className={cn(
          "gap-0.5",
          getStatusColorForCurrentRole.background,
          "border-transparent",
          getStatusColorForCurrentRole.color,
        )}
      >
        {statusFound.Icon && (
          <statusFound.Icon
            className={cn(
              "transform scale-50 -m-0.5",
              getStatusColorForCurrentRole.color,
            )}
          />
        )}
        {statusFound.label}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1",
        getStatusColorForCurrentRole.background,
        "border-transparent",
        getStatusColorForCurrentRole.color,
      )}
    >
      {statusFound.Icon && (
        <statusFound.Icon
          className={cn("h-4 w-4", getStatusColorForCurrentRole.color)}
        />
      )}
      {statusFound.label}
    </Badge>
  );
}
